/**
 * Deal Workflow Service
 *
 * Stage transitions, needs-attention evaluation, and activity logging
 * for the Deal Workspace system.
 */

import { db } from '@/firebase';
import {
  doc,
  updateDoc,
  arrayUnion,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  deleteField,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import {
  DealStage,
  DealCategory,
  DEAL_STAGE_ORDER,
  DEAL_CATEGORY_LABELS,
} from '@/types/theo-workflow.types';
import type {
  Deal,
  ManualFlag,
  ServiceModuleData,
  ReviewedQuoteItem,
} from '@/types/theo-workflow.types';
import type { DealSnapshot } from '@/types/schema';
import { assertDealMayEnterStage } from './deal-stage-guard';
// Phase 0 (trade-pack architecture): service-layer code can't use the pack
// hooks (no React context). Stays on the static SERVICE_CONFIGS map until
// Phase 1+ ships pack hydration through services. In Phase 0 this is a no-op
// — the static map IS the canonical telco-installer data, so pack-driven and
// static reads return identical values.
import { SERVICE_CONFIGS } from '@/data/service-configs';
import { seedDealIntakeNotes } from './deal-intake-notes.service';
import { fetchPinnedNotesForEntity } from './notes.service';
import { getCheckpointsForNewJob } from '@/services/checklist-template.service';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { stripUndefinedDeep } from '@/utils/firestore';
import { logCustomerActivity } from '@/services/aggregate-customer.service';
import { buildJobEvent } from '@/services/job-event.service';
import { summariseScopeLabour } from '@/services/quote-pricing';
import { generateProvisioningFromQuote, getProvisioningItemsByDeal } from '@/services/provisioning.service';
import { geocodeAddress } from '@/services/geocode.service';
import { JOB_FALLBACK_COORDS } from '@/data/map-config';
import type { PlaygroundQuoteItem } from '@/types/schema';

/**
 * Scheduling-guidance shape captured on the deal by the office scheduling
 * modal. Not part of the canonical Deal type yet, so accessed via a typed
 * view rather than `any`.
 */
interface DealSchedulingGuidance {
  estimatedDuration?: number;
  preferredTechId?: string;
  preferredTechName?: string;
  preferredTechIds?: string[];
  preferredTechNames?: string[];
  customerPreferredDate?: string;
  customerPreferredTime?: string;
  scheduleByDate?: string;
  schedulingNotes?: string;
}

/** Deal augmented with the office-only scheduling-guidance block. */
type DealWithGuidance = Deal & { schedulingGuidance?: DealSchedulingGuidance };

// ---------------------------------------------------------------------------
// Geocoding helper for new jobs created from deals
// ---------------------------------------------------------------------------

/**
 * Resolve coordinates for a new job off a deal. Priority:
 *   1. Explicit lat/lng already on the deal (earlier geocode hit).
 *   2. Google Geocoding API against the deal's full address (cached).
 *   3. The shared Bowral fallback — map backfill will retry later.
 */
async function resolveJobCoords(
  deal: Deal,
  fallbackAddress?: string,
): Promise<{ lat: number; lng: number; formatted?: string }> {
  const dealExtra = deal as Deal & { lat?: number; lng?: number; jobState?: string };
  const dealLat = dealExtra.lat;
  const dealLng = dealExtra.lng;
  if (typeof dealLat === 'number' && typeof dealLng === 'number') {
    return { lat: dealLat, lng: dealLng };
  }

  const parts = [
    deal.jobAddress,
    deal.jobCity,
    dealExtra.jobState,
    fallbackAddress,
  ].filter(Boolean);
  const address = parts.join(', ');
  if (address) {
    const r = await geocodeAddress(address).catch(() => null);
    if (r) return { lat: r.lat, lng: r.lng, formatted: r.formattedAddress };
  }
  return { lat: JOB_FALLBACK_COORDS.lat, lng: JOB_FALLBACK_COORDS.lng };
}

// ============================================================================
// STAGE TRANSITIONS
// ============================================================================

/**
 * Advance a deal to the next stage in the workflow.
 *
 * `expectedFromStage` is a compare-and-advance guard: the deal is only moved
 * when it is STILL at that stage at the moment of the read inside this
 * function. Callers that pre-check the stage themselves ("only advance if
 * still PRESITE") must pass it — their own `getDoc` happens strictly before
 * this one, so a concurrent advance landing in between made the pre-check
 * stale and this function then advanced from the NEW stage, one step too far.
 *
 * That is exactly what pushed pre-site deals past QUOTING into PROPOSAL_SENT:
 * the tech job card and the job side-effect chain both completed a pre-site,
 * both read PRESITE, and the second one to run advanced QUOTING →
 * PROPOSAL_SENT. Mismatch is a benign no-op (returns the deal's current
 * stage), never a throw — a duplicate advance is not an error condition.
 */
export async function advanceDealStage(
  dealId: string,
  userId: string,
  userName: string,
  notes?: string,
  expectedFromStage?: DealStage
): Promise<DealStage> {
  const dealRef = doc(db, 'deals', dealId);
  const snap = await getDoc(dealRef);
  if (!snap.exists()) throw new Error(`Deal ${dealId} not found`);

  const deal = { id: snap.id, ...snap.data() } as Deal;

  if (expectedFromStage && deal.stage !== expectedFromStage) {
    console.log(
      `[advanceDealStage] Deal ${dealId} is at ${deal.stage}, expected ${expectedFromStage} — skipping advance`
    );
    return deal.stage;
  }

  const currentIndex = DEAL_STAGE_ORDER.indexOf(deal.stage);

  if (currentIndex === -1 || currentIndex >= DEAL_STAGE_ORDER.length - 1) {
    throw new Error(`Cannot advance from stage ${deal.stage}`);
  }

  const nextStage = DEAL_STAGE_ORDER[currentIndex + 1];

  // Skip PRESITE if not required
  if (nextStage === DealStage.PRESITE && !deal.presiteRequired) {
    return moveDealToStage(dealId, DealStage.QUOTING, userId, userName, notes);
  }

  return moveDealToStage(dealId, nextStage, userId, userName, notes);
}

// The manual-WON guard (docs/proposal-v2/S10.md §3.3) — callers catch DealStageBlockedError from here.
export { DealStageBlockedError, isDealStageBlockedError, dealStageBlockedReason } from './deal-stage-guard';

/** Read the deal and throw `DealStageBlockedError` when it may not enter `stage` (WON only — no read otherwise). */
async function assertStageAllowed(dealId: string, stage: DealStage): Promise<void> {
  if (stage !== DealStage.WON) return;
  const snap = await getDoc(doc(db, 'deals', dealId));
  assertDealMayEnterStage(snap.exists() ? (snap.data() as Deal) : null, stage);
}

/**
 * Move a deal to a specific stage — the one chokepoint for stage moves.
 * Throws `DealStageBlockedError` (nothing written) when the deal may not
 * enter `newStage` (`dealStageBlockedReason`).
 */
export async function moveDealToStage(
  dealId: string,
  newStage: DealStage,
  userId: string,
  userName: string,
  notes?: string
): Promise<DealStage> {
  await assertStageAllowed(dealId, newStage);
  const dealRef = doc(db, 'deals', dealId);
  const now = new Date().toISOString();

  const stageEntry: Record<string, unknown> = {
    stage: newStage,
    enteredAt: now,
    completedBy: userId,
  };
  if (notes) stageEntry.notes = notes;

  const activityEntry: Record<string, unknown> = {
    id: crypto.randomUUID(),
    timestamp: now,
    actor: userId,
    actorName: userName,
    action: `Moved to ${newStage}`,
    automated: false,
  };
  if (notes) activityEntry.details = notes;

  const updates: Record<string, unknown> = {
    stage: newStage,
    stageHistory: arrayUnion(stageEntry),
    activityLog: arrayUnion(activityEntry),
    updatedAt: Timestamp.now(),
    nextAction: getNextActionForStage(newStage),
  };

  if (newStage === DealStage.WON) {
    updates.wonAt = Timestamp.now();
  }
  if (newStage === DealStage.LOST) {
    updates.lostAt = Timestamp.now();
  }

  // Clear the H2 visit-loop idempotency marker on any transition to a
  // non-SCHEDULING stage. A deliberate revert from REVIEW back to SCHEDULING
  // (or any other path that cycles through a non-SCHEDULING stage) wants
  // fresh install jobs on re-entry.
  if (newStage !== DealStage.SCHEDULING) {
    updates.installJobsCreatedAt = deleteField();
  }

  // C6/M1: clear any prior stage-transition error at the start of every
  // transition. Side-effect catch blocks below will re-write it on failure;
  // a clean run leaves it absent.
  updates.stageTransitionError = deleteField();

  await updateDoc(dealRef, updates);

  // Customer 360 audit log — fire-and-forget so a logging failure NEVER
  // rolls back the stage save. Dedup inside the service prevents doubles
  // when scripted flows fire the same transition repeatedly. Day 5.
  void (async () => {
    try {
      const snap = await getDoc(dealRef);
      if (!snap.exists()) return;
      const fresh = snap.data() as Deal;
      if (!fresh.customerId) return;
      await logCustomerActivity({
        customerId: fresh.customerId,
        actorId: userId,
        actorName: userName,
        entryType: 'stage_change',
        summary: `Deal "${fresh.title || fresh.customerName || 'Untitled'}" → ${newStage}`,
        sourceEntity: 'deal',
        sourceId: dealId,
        timestamp: now,
      });
    } catch (err) {
      console.error('[moveDealToStage] customer audit log failed:', err);
    }
  })();

  // NOTE: Service creation is deferred to BILLING stage (triggered by install job completion).
  // This gives the team real data about what was actually installed before creating service records.

  // Auto-create pre-site job when deal enters PRESITE stage
  if (newStage === DealStage.PRESITE) {
    try {
      await createPresiteJobFromDeal(dealId, userId, userName);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('Failed to auto-create pre-site job:', err);
      // Don't block stage transition if job creation fails — but record so
      // ops can see the deal is in PRESITE without a pre-site job (C6/M1).
      await recordStageTransitionError(dealRef, newStage, 'presite-job-creation', message);
    }
  }

  // Auto-generate provisioning items when deal enters PROVISIONING stage
  // This ensures items exist even when quote review was skipped (e.g. Better Proposals flow)
  if (newStage === DealStage.PROVISIONING) {
    try {
      // Re-fetch deal to get latest data (stage was just updated above)
      const freshSnap = await getDoc(dealRef);
      const freshDeal = { id: freshSnap.id, ...freshSnap.data() } as Deal;

      // Check if provisioning items already exist (e.g. from quote review completion)
      const existingItems = await getProvisioningItemsByDeal(dealId);
      if (existingItems.length === 0) {
        // Extract quote items from deal — prioritise reviewed items (most authoritative).
        // Uses waterfall: if one priority yields zero items, fall through to the next.
        let quoteItems: PlaygroundQuoteItem[] = [];

        // Priority 1: quoteReviewedItems (approved + visible — most authoritative)
        // Items flagged hideFromProvisioning stay in deal value + billing but are
        // suppressed from provisioning_items (and therefore from the tech job).
        if (freshDeal.quoteReviewedItems && freshDeal.quoteReviewedItems.length > 0) {
          const approved = freshDeal.quoteReviewedItems.filter(
            (ri: ReviewedQuoteItem) => ri.approved && !ri.hideFromProvisioning,
          );
          quoteItems = approved.map((ri: ReviewedQuoteItem) => {
            const unitPrice = ri.revisedUnitPrice || ri.originalUnitPrice;
            return {
              id: ri.originalItemId,
              name: ri.name,
              category: ri.category,
              quantity: ri.revisedQuantity || ri.originalQuantity,
              unitCost: unitPrice,
              unitPrice,
              totalPrice: ri.revisedTotalPrice || ri.originalTotalPrice,
              billingType: ri.billingType,
              pipelineStatus: 'quoted' as const,
              sourceType: 'manual' as const,
              addedAt: ri.reviewedAt,
              updatedAt: ri.reviewedAt,
            };
          });
        }
        // Priority 2: quoteScopes (direct scoped items on the deal)
        if (quoteItems.length === 0 && freshDeal.quoteScopes && freshDeal.quoteScopes.length > 0) {
          quoteItems = freshDeal.quoteScopes.flatMap((scope) => scope.items || []);
        }
        // Priority 3: latest quoteVersion items
        if (quoteItems.length === 0 && freshDeal.quoteVersions && freshDeal.quoteVersions.length > 0) {
          const latestVersion = freshDeal.quoteVersions[freshDeal.quoteVersions.length - 1];
          if (latestVersion.scopes && latestVersion.scopes.length > 0) {
            quoteItems = latestVersion.scopes.flatMap((scope) => scope.items || []);
          } else if (latestVersion.items && latestVersion.items.length > 0) {
            quoteItems = latestVersion.items;
          }
        }
        // Priority 4: proposalAcceptedItems from Better Proposals webhook
        if (quoteItems.length === 0 && freshDeal.proposalAcceptedItems && freshDeal.proposalAcceptedItems.length > 0) {
          const now = new Date().toISOString();
          quoteItems = freshDeal.proposalAcceptedItems
            .filter((ai) => ai.isSelected !== false)
            .map((ai) => ({
              id: `bp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              name: ai.label,
              category: ai.tableTitle || undefined,
              quantity: ai.quantity || 1,
              unitCost: ai.unitCost || 0,
              unitPrice: ai.unitCost || 0,
              totalPrice: ai.cost || 0,
              billingType: (ai.recurringType === 'One Time Payment' ? 'one-time' : 'recurring') as 'one-time' | 'recurring',
              pipelineStatus: 'approved' as const,
              sourceType: 'manual' as const,
              addedAt: now,
              updatedAt: now,
            }));
        }

        if (quoteItems.length > 0) {
          await generateProvisioningFromQuote(dealId, quoteItems, freshDeal);
          console.log(`[Deal Workflow] Auto-generated provisioning items for deal ${dealId}`);
        } else {
          console.warn(`[Deal Workflow] Deal ${dealId} entered PROVISIONING with no quote items to generate from`);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[Deal Workflow] Failed to auto-generate provisioning items:', err);
      // Don't block stage transition if provisioning generation fails — but
      // surface so ops can see PROVISIONING without items (C6/M1).
      await recordStageTransitionError(dealRef, newStage, 'provisioning-generation', message);
    }
  }

  // Auto-create install jobs when deal enters SCHEDULING stage
  // Uses installPlan.visitCount to create the right number of jobs
  // When visitPlans exist, each job gets visit-specific name, duration, techs, and items
  if (newStage === DealStage.SCHEDULING) {
    try {
      // Re-fetch deal to get latest installPlan data
      const schedSnap = await getDoc(dealRef);
      const schedDeal = { id: schedSnap.id, ...schedSnap.data() } as Deal;

      // H2 idempotency: if the visit loop already ran for the current
      // SCHEDULING entry, skip it. The marker is cleared by moveDealToStage
      // on any transition to a non-SCHEDULING stage (advance, revert, LOST),
      // so a deliberate revert+re-enter still creates fresh jobs.
      // Without this, a duplicate moveDealToStage(SCHEDULING) call would have
      // iter 0 short-circuit (returning the existing installJobId via
      // createJobFromDeal's inner guard) but iters 1..N create new jobs —
      // adding (visitCount - 1) duplicates per repeat call.
      if (schedDeal.installJobsCreatedAt) {
        console.log(
          `[Deal Workflow] Install jobs already created at ${schedDeal.installJobsCreatedAt} for deal ${dealId} — skipping visit loop`,
        );
      } else {
        const visitCount = schedDeal.installPlan?.visitCount ?? 1;
        const visitPlans = schedDeal.installPlan?.visitPlans;
        // Duration precedence: the office's install plan (already pre-filled
        // from the quote in DealSchedulingModal) → else the quoted labour
        // hours directly (covers the parallel Schedule & Provision flow that
        // skips the modal) → else a 2hr default.
        const quotedForDeal = summariseScopeLabour(schedDeal.quoteScopes);
        const durationMinutes =
          schedDeal.installPlan?.hoursPerVisit != null
            ? schedDeal.installPlan.hoursPerVisit * 60
            : quotedForDeal.hours > 0
              ? Math.round(quotedForDeal.hours * 60)
              : 120;

        // C5: per-visit idempotency. Query existing install jobs for this
        // deal so a partial-failure retry (or any retry where the H2 marker
        // is unset) only creates the missing visits. Without this, iter 0
        // would short-circuit via createJobFromDeal's inner guard but
        // iters 1..N would re-create jobs that already succeeded.
        const existingInstallJobsSnap = await getDocs(query(
          collection(db, 'jobs'),
          where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
          where('dealId', '==', dealId),
          where('type', '==', 'Install'),
        ));
        const existingVisitNumbers = new Set<number>();
        let hasAnyExistingInstall = false;
        for (const jdoc of existingInstallJobsSnap.docs) {
          const data = jdoc.data() as { deleted?: boolean; visitNumber?: number };
          if (data.deleted) continue;
          hasAnyExistingInstall = true;
          if (typeof data.visitNumber === 'number') {
            existingVisitNumbers.add(data.visitNumber);
          }
        }

        // C5: per-iteration error capture so a single visit's failure
        // (network blip, permission denied, validation throw) doesn't
        // abort the rest of the visits. Failures are recorded on the deal
        // so ops can see + retry. Pre-fix, the outer try/catch swallowed
        // a single throw and left visits 0..N-1 written with no signal.
        const failedVisits: Array<{ visitNumber: number; message: string }> = [];
        let createdCount = 0;
        let skippedCount = 0;

        for (let i = 0; i < visitCount; i++) {
          const visitNumber = i + 1;
          const plan = visitPlans?.[i];
          const schedule = {
            date: '',
            time: '09:00',
            duration: plan?.durationMinutes ?? durationMinutes,
            techIds: plan?.preferredTechIds,
            techNames: plan?.preferredTechNames,
          };
          const visitMeta = plan ? {
            visitName: plan.name,
            visitNumber,
            totalVisits: visitCount,
            allocatedItemIds: plan.allocatedItemIds,
            allocatedItemNames: plan.allocatedItemNames,
          } : visitCount > 1 ? {
            visitNumber,
            totalVisits: visitCount,
          } : undefined;

          // Idempotency: skip visits that already exist. Multi-visit deals
          // match by visitNumber. Single-visit deals (no visitNumber on the
          // job) skip if any non-deleted install job exists — same semantic
          // as createJobFromDeal's inner installJobId guard.
          const alreadyExists =
            (visitCount > 1 && existingVisitNumbers.has(visitNumber)) ||
            (visitCount === 1 && hasAnyExistingInstall);
          if (alreadyExists) {
            console.log(
              `[Deal Workflow] Visit ${visitNumber} already exists for deal ${dealId} — skipping`,
            );
            skippedCount++;
            continue;
          }

          try {
            await createJobFromDeal(
              dealId, userId, userName,
              schedule,
              i > 0, // allowAdditional for visits after the first
              visitMeta,
            );
            createdCount++;
          } catch (visitErr) {
            const msg = visitErr instanceof Error ? visitErr.message : String(visitErr);
            console.error(
              `[Deal Workflow] Failed to create visit ${visitNumber} for deal ${dealId}:`,
              visitErr,
            );
            failedVisits.push({ visitNumber, message: msg });
          }
        }

        const dealUpdate: Record<string, unknown> = {};
        if (failedVisits.length === 0) {
          // Every visit either committed cleanly or was idempotently skipped.
          // Stamp the marker (so any duplicate stage-entry call short-circuits
          // via the H2 fast path) and clear any prior partial-failure record.
          dealUpdate.installJobsCreatedAt = new Date().toISOString();
          dealUpdate.installJobsCreationError = deleteField();
        } else {
          // Partial failure — record which visits failed so ops can retry.
          // Don't stamp installJobsCreatedAt; a future moveDealToStage(SCHEDULING)
          // will re-run the loop and idempotency will fill in the missing visits.
          dealUpdate.installJobsCreationError = {
            at: new Date().toISOString(),
            failedVisits: failedVisits.map((f) => f.visitNumber),
            message: failedVisits
              .map((f) => `visit ${f.visitNumber}: ${f.message}`)
              .join(' | '),
          };
        }
        await updateDoc(dealRef, dealUpdate);

        if (visitCount > 1) {
          console.log(
            `[Deal Workflow] Visit loop for deal ${dealId}: ${createdCount} created, ${skippedCount} skipped, ${failedVisits.length} failed`,
          );
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('Failed to auto-create install job(s):', err);
      // Outer-level failure (e.g. getDoc threw before the loop ran). The
      // visit loop's per-iteration tracking writes to installJobsCreationError
      // for partial-loop failures; this records the broader "couldn't even
      // start the loop" case so ops still sees something (C6/M1).
      await recordStageTransitionError(dealRef, newStage, 'install-jobs-loop', message);
    }
  }

  return newStage;
}

/**
 * Persist a stage-transition side-effect failure to the deal so ops can
 * see and remediate. Fire-and-forget-safe: a failure here is logged and
 * swallowed (we'd rather lose the marker than crash the caller's catch).
 */
async function recordStageTransitionError(
  dealRef: import('firebase/firestore').DocumentReference,
  stage: DealStage,
  source: string,
  message: string,
): Promise<void> {
  try {
    await updateDoc(dealRef, {
      stageTransitionError: {
        at: new Date().toISOString(),
        stage,
        source,
        message,
      },
    });
  } catch (writeErr) {
    console.error(
      '[Deal Workflow] Failed to record stageTransitionError on deal (non-fatal):',
      writeErr,
    );
  }
}

/**
 * Revert a deal to a previous stage. Rewinding INTO WON (from provisioning
 * or later) passes the same manual-WON guard as `moveDealToStage`.
 */
export async function revertDealStage(
  dealId: string,
  targetStage: DealStage,
  userId: string,
  userName: string,
  reason: string
): Promise<void> {
  await assertStageAllowed(dealId, targetStage);
  const now = new Date().toISOString();

  const activityEntry: Record<string, unknown> = {
    id: crypto.randomUUID(),
    timestamp: now,
    actor: userId,
    actorName: userName,
    action: `Reverted to ${targetStage}`,
    details: reason,
    automated: false,
  };

  const revertUpdates: Record<string, unknown> = {
    stage: targetStage,
    stageHistory: arrayUnion({
      stage: targetStage,
      enteredAt: now,
      completedBy: userId,
      notes: `Reverted: ${reason}`,
    }),
    activityLog: arrayUnion(activityEntry),
    updatedAt: Timestamp.now(),
    nextAction: getNextActionForStage(targetStage),
  };
  // Mirror the moveDealToStage clear: when reverting away from SCHEDULING the
  // visit-loop marker is no longer relevant, and a future advance back to
  // SCHEDULING wants fresh jobs. Safe no-op when the marker wasn't set.
  if (targetStage !== DealStage.SCHEDULING) {
    revertUpdates.installJobsCreatedAt = deleteField();
  }

  await updateDoc(doc(db, 'deals', dealId), revertUpdates);

  // Auto-create pre-site job when reverting back to PRESITE
  if (targetStage === DealStage.PRESITE) {
    try {
      await createPresiteJobFromDeal(dealId, userId, userName);
    } catch (err) {
      console.error('Failed to auto-create pre-site job on revert:', err);
    }
  }

  // Auto-create install job when reverting back to SCHEDULING (e.g. return
  // visit needed). allowAdditional=true so a job is ALWAYS raised even though
  // the deal already carries completed install jobs. A failure here used to be
  // swallowed silently, leaving the deal in SCHEDULING with nothing to
  // schedule — now it's recorded on the deal so ops can see + retry.
  if (targetStage === DealStage.SCHEDULING) {
    try {
      await createJobFromDeal(dealId, userId, userName, undefined, true);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('Failed to auto-create install job on revert:', err);
      await recordStageTransitionError(
        doc(db, 'deals', dealId), targetStage, 'revert-install-job', message,
      );
    }
  }
}

/**
 * Raise install job(s) for a deal that's (re)entering SCHEDULING for a return
 * visit — i.e. via "Send back to scheduling". Unlike the forward
 * `moveDealToStage` visit loop, this ALWAYS creates fresh jobs: it does NOT
 * consult the `installJobsCreatedAt` marker and does NOT skip visits that
 * already exist, because a return visit is meant to sit alongside the
 * already-completed install jobs. Honours `installPlan.visitCount` +
 * `visitPlans` so the office's "how many visits / techs / duration" choice
 * (captured in the scheduling modal) is respected.
 *
 * Per-iteration error capture: one visit failing (network blip, permission
 * denied, dangling customer link) never aborts the rest. The outcome is
 * recorded on the deal (`installJobsCreationError`) so ops can see + retry,
 * and returned so the caller can surface it.
 */
async function raiseReturnInstallJobs(
  dealId: string,
  userId: string,
  userName: string,
): Promise<{ created: number; failed: Array<{ visitNumber: number; message: string }> }> {
  const dealRef = doc(db, 'deals', dealId);
  const snap = await getDoc(dealRef);
  if (!snap.exists()) throw new Error(`Deal ${dealId} not found`);
  const deal = { id: snap.id, ...snap.data() } as Deal;

  const visitCount = deal.installPlan?.visitCount ?? 1;
  const visitPlans = deal.installPlan?.visitPlans;
  // Duration precedence mirrors the forward visit loop: install plan →
  // quoted labour hours → 2hr default.
  const quotedLabour = summariseScopeLabour(deal.quoteScopes);
  const durationMinutes =
    deal.installPlan?.hoursPerVisit != null
      ? deal.installPlan.hoursPerVisit * 60
      : quotedLabour.hours > 0
        ? Math.round(quotedLabour.hours * 60)
        : 120;

  const failed: Array<{ visitNumber: number; message: string }> = [];
  let created = 0;

  for (let i = 0; i < visitCount; i++) {
    const visitNumber = i + 1;
    const plan = visitPlans?.[i];
    const schedule = {
      date: '',
      time: '09:00',
      duration: plan?.durationMinutes ?? durationMinutes,
      techIds: plan?.preferredTechIds,
      techNames: plan?.preferredTechNames,
    };
    const visitMeta = plan ? {
      visitName: plan.name,
      visitNumber,
      totalVisits: visitCount,
      allocatedItemIds: plan.allocatedItemIds,
      allocatedItemNames: plan.allocatedItemNames,
    } : visitCount > 1 ? {
      visitNumber,
      totalVisits: visitCount,
    } : undefined;

    try {
      // allowAdditional=true: a return visit ALWAYS creates a new job.
      await createJobFromDeal(dealId, userId, userName, schedule, true, visitMeta);
      created++;
    } catch (visitErr) {
      const message = visitErr instanceof Error ? visitErr.message : String(visitErr);
      console.error(
        `[Deal Workflow] Failed to raise return install visit ${visitNumber} for deal ${dealId}:`,
        visitErr,
      );
      failed.push({ visitNumber, message });
    }
  }

  // Surface the outcome on the deal so ops can see/retry a partial failure.
  try {
    if (failed.length === 0) {
      await updateDoc(dealRef, {
        // Mark that jobs exist for this SCHEDULING entry so a stray forward
        // moveDealToStage(SCHEDULING) short-circuits its own visit loop.
        installJobsCreatedAt: new Date().toISOString(),
        installJobsCreationError: deleteField(),
      });
    } else {
      await updateDoc(dealRef, {
        installJobsCreationError: {
          at: new Date().toISOString(),
          failedVisits: failed.map((f) => f.visitNumber),
          message: failed.map((f) => `visit ${f.visitNumber}: ${f.message}`).join(' | '),
        },
      });
    }
  } catch (writeErr) {
    console.error('[Deal Workflow] Failed to record return-visit job outcome (non-fatal):', writeErr);
  }

  return { created, failed };
}

/**
 * Send a deal BACK to SCHEDULING (typically from REVIEW, when an install needs
 * a return visit) and ALWAYS raise the install job(s) to schedule. Honours the
 * office's install-plan choice (visit count / techs / duration) which the
 * scheduling modal saves to `deal.installPlan` immediately before this runs.
 *
 * Throws if the stage moved but NOT a single job could be raised, so the caller
 * can surface the failure instead of silently leaving the office with a deal in
 * SCHEDULING and nothing to schedule.
 */
export async function sendDealBackToScheduling(
  dealId: string,
  userId: string,
  userName: string,
  reason: string,
): Promise<{ created: number; failed: Array<{ visitNumber: number; message: string }> }> {
  const now = new Date().toISOString();
  const trimmedReason = reason.trim() || 'Return visit needed';

  await updateDoc(doc(db, 'deals', dealId), {
    stage: DealStage.SCHEDULING,
    stageHistory: arrayUnion({
      stage: DealStage.SCHEDULING,
      enteredAt: now,
      completedBy: userId,
      notes: `Sent back to scheduling: ${trimmedReason}`,
    }),
    activityLog: arrayUnion({
      id: crypto.randomUUID(),
      timestamp: now,
      actor: userId,
      actorName: userName,
      action: 'Sent back to scheduling',
      details: trimmedReason,
      automated: false,
    }),
    updatedAt: Timestamp.now(),
    nextAction: getNextActionForStage(DealStage.SCHEDULING),
  });

  const result = await raiseReturnInstallJobs(dealId, userId, userName);
  if (result.created === 0) {
    throw new Error(
      'Deal moved to scheduling, but no install job could be raised. Open the deal and use "Schedule another visit" to retry.',
    );
  }
  return result;
}

// ============================================================================
// ACTIVITY LOGGING
// ============================================================================

/**
 * Add an activity log entry to a deal
 */
export async function addActivityLogEntry(
  dealId: string,
  actor: string,
  actorName: string,
  action: string,
  details?: string,
  automated = false
): Promise<void> {
  const entry: Record<string, unknown> = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    actor,
    actorName,
    action,
    automated,
  };
  // Only include details if defined (Firestore rejects undefined in arrayUnion)
  if (details !== undefined) entry.details = details;

  await updateDoc(doc(db, 'deals', dealId), {
    activityLog: arrayUnion(entry),
    updatedAt: Timestamp.now(),
  });
}

// ============================================================================
// MANUAL FLAGS
// ============================================================================

/**
 * Add a manual flag to a deal
 */
export async function addManualFlag(
  dealId: string,
  flaggedBy: string,
  flaggedByName: string,
  note: string
): Promise<void> {
  const flag: ManualFlag = {
    id: crypto.randomUUID(),
    flaggedBy,
    flaggedByName,
    note,
    flaggedAt: new Date().toISOString(),
    resolved: false,
  };

  await updateDoc(doc(db, 'deals', dealId), {
    manualFlags: arrayUnion(flag),
    needsAttention: true,
    needsAttentionReasons: arrayUnion(`Manual flag: ${note}`),
    needsAttentionSeverity: 'yellow',
    updatedAt: Timestamp.now(),
  });

  await addActivityLogEntry(
    dealId,
    flaggedBy,
    flaggedByName,
    'Flagged deal',
    note,
    false
  );
}

/**
 * Resolve a manual flag
 */
export async function resolveManualFlag(
  dealId: string,
  flagId: string,
  resolvedBy: string
): Promise<void> {
  const dealRef = doc(db, 'deals', dealId);
  const snap = await getDoc(dealRef);
  if (!snap.exists()) return;

  const deal = snap.data() as Deal;
  const flags = (deal.manualFlags || []).map((f) =>
    f.id === flagId
      ? { ...f, resolved: true, resolvedAt: new Date().toISOString(), resolvedBy }
      : f
  );

  const hasUnresolved = flags.some((f) => !f.resolved);

  await updateDoc(dealRef, {
    manualFlags: flags,
    needsAttention: hasUnresolved,
    updatedAt: Timestamp.now(),
  });
}

// ============================================================================
// NEEDS ATTENTION EVALUATION
// ============================================================================

export interface AttentionCheck {
  reason: string;
  severity: 'yellow' | 'red';
}

/**
 * Evaluate needs-attention triggers for a deal
 */
export function evaluateNeedsAttention(deal: Deal): AttentionCheck[] {
  const checks: AttentionCheck[] = [];

  // Terminal stages never need attention
  if (deal.stage === DealStage.LOST || deal.stage === DealStage.COMPLETED) {
    return checks;
  }

  const now = new Date();

  // Proposal sent, no response (5/10 business days)
  if (deal.stage === DealStage.PROPOSAL_SENT) {
    const sentEntry = (deal.stageHistory || [])
      .filter((h) => h.stage === DealStage.PROPOSAL_SENT)
      .pop();
    if (sentEntry) {
      const sentDate = new Date(sentEntry.enteredAt);
      const daysSinceSent = Math.floor(
        (now.getTime() - sentDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceSent >= 10) {
        checks.push({ reason: 'Proposal sent 10+ days ago, no response', severity: 'red' });
      } else if (daysSinceSent >= 5) {
        checks.push({ reason: 'Proposal sent 5+ days ago, no response', severity: 'yellow' });
      }
    }
  }

  // All gear ready, not scheduled (3 days)
  if (deal.stage === DealStage.PROVISIONING) {
    // This would need provisioning items check -- simplified here
  }

  // Zero activity for 30 days
  const log = deal.activityLog || [];
  const lastActivity = log.length > 0 ? log[log.length - 1] : undefined;
  if (lastActivity) {
    const lastDate = new Date(lastActivity.timestamp);
    const daysSince = Math.floor(
      (now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysSince >= 30) {
      checks.push({ reason: 'No activity for 30+ days (stale)', severity: 'red' });
    }
  }

  // Awaiting funds — red severity, shows dollar amount
  if (deal.awaitingFunds && !deal.fundingReceivedAt) {
    const amt = deal.awaitingFundsAmount;
    const formatted = amt ? `$${amt.toLocaleString()}` : '';
    checks.push({
      reason: `Awaiting Funds${formatted ? ` — ${formatted}` : ''}`,
      severity: 'red',
    });
  }

  // Unresolved manual flags
  const unresolvedFlags = (deal.manualFlags || []).filter((f) => !f.resolved);
  for (const flag of unresolvedFlags) {
    checks.push({ reason: `Flag: ${flag.note}`, severity: 'yellow' });
  }

  return checks;
}

// ============================================================================
// DEAL → JOB CONVERSION
// ============================================================================

/**
 * Build a DealSnapshot from a deal document.
 * This embeds all deal context onto the job so schedulers and techs
 * can assess scope without fetching the deal separately.
 *
 * Async because `pinnedNotes` has to read the unified `notes` collection —
 * see the pinned-notes block below for why the legacy array isn't enough.
 */
async function buildDealSnapshot(deal: Deal): Promise<DealSnapshot> {
  const selectedServices = deal.selectedServices ?? [];
  const serviceLabels = selectedServices
    .map((s: DealCategory) => DEAL_CATEGORY_LABELS[s] ?? s)
    .filter(Boolean);

  // Build a list of reasons why presite is required
  const presiteReasons: string[] = [];
  for (const svc of selectedServices) {
    const config = SERVICE_CONFIGS[svc];
    if (!config) continue;
    if (config.presiteDefault === 'always') {
      presiteReasons.push(`${config.label} always requires pre-site`);
    } else if (config.presiteDefault === 'conditional') {
      const moduleData = deal.serviceModules?.[svc];
      if (moduleData?.presiteRequired) {
        presiteReasons.push(`${config.label}: ${config.presiteCondition ?? 'conditional'}`);
      }
    }
  }

  // Build serializable service modules (strip functions/classes)
  const serviceModules: DealSnapshot['serviceModules'] = {};
  if (deal.serviceModules) {
    for (const svc of selectedServices) {
      const mod = deal.serviceModules[svc] as ServiceModuleData | undefined;
      if (mod) {
        serviceModules[svc] = {
          presiteRequired: mod.presiteRequired ?? false,
          presiteCompleted: mod.presiteCompleted ?? false,
          intakeFields: (mod.intakeFields ?? {}) as unknown as Record<string, unknown>,
          presiteFields: mod.presiteFields ?? {},
          status: mod.status ?? 'intake',
        };
      }
    }
  }

  // Get active quote totals if available
  let quoteTotal: number | undefined;
  let monthlyTotal: number | undefined;
  if (deal.quoteVersions && deal.quoteVersions.length > 0) {
    const activeVersion = deal.activeQuoteVersion !== undefined
      ? deal.quoteVersions.find((v) => v.version === deal.activeQuoteVersion)
      : deal.quoteVersions[deal.quoteVersions.length - 1];
    if (activeVersion) {
      quoteTotal = activeVersion.total;
      monthlyTotal = activeVersion.monthlyTotal;
    }
  }

  // Build snapshot object, excluding undefined values (Firestore doesn't allow them)
  const snapshot: DealSnapshot = {
    dealId: deal.id ?? '',
    title: deal.title,
    stage: deal.stage,
    presiteRequired: deal.presiteRequired ?? false,
    selectedServices,
    serviceLabels,
    snapshotAt: new Date().toISOString(),
  };

  // Only include optional fields if they have values
  if (deal.value !== undefined) snapshot.value = deal.value;
  if (deal.complexity) snapshot.complexity = deal.complexity;
  if (deal.contactName) snapshot.contactName = deal.contactName;
  if (deal.contactPhone) snapshot.contactPhone = deal.contactPhone;
  if (deal.contactEmail) snapshot.contactEmail = deal.contactEmail;
  if (deal.customerName) snapshot.customerName = deal.customerName;
  if (Object.keys(serviceModules).length > 0) snapshot.serviceModules = serviceModules;
  if (presiteReasons.length > 0) snapshot.presiteReason = presiteReasons.join('; ');
  if (quoteTotal !== undefined) snapshot.quoteTotal = quoteTotal;
  if (monthlyTotal !== undefined) snapshot.monthlyTotal = monthlyTotal;
  if (deal.activeQuoteVersion !== undefined) snapshot.activeQuoteVersion = deal.activeQuoteVersion;
  if (deal.notes) snapshot.notes = deal.notes;
  // Include ALL deal notes so AI briefing has full context
  if (deal.dealNotes && deal.dealNotes.length > 0) {
    snapshot.dealNotes = deal.dealNotes.map((n) => ({
      content: n.content,
      author: n.createdByName,
      createdAt: n.createdAt,
      pinned: n.pinned,
    }));
  }

  // ── PINNED NOTES ─────────────────────────────────────────────────────────
  // Two sources, and BOTH are required.
  //
  // `deal.dealNotes[]` is the legacy array — read-only history now; nothing
  // has written to it since notes moved to the unified `notes` collection.
  // Reading only that array meant every pin made after the migration was
  // invisible to `snapshot.pinnedNotes`, and so to everything downstream of
  // it: the tech job brief's "PINNED BY OFFICE" block (job-brief.service),
  // job insights' "Pinned deal note" source, the V3 "Pinned from Office"
  // card, and the scheduler panel's snapshot fallback. The office pinned the
  // note and the field never saw it.
  //
  // Best-effort: a notes read that fails must not take job creation with it,
  // so it degrades to the legacy array rather than throwing.
  const pinnedFromLegacy = (deal.dealNotes ?? [])
    .filter((n) => n.pinned)
    .map((n) => ({
      content: n.content,
      author: n.createdByName,
      createdAt: n.createdAt,
    }));

  let pinnedFromNotes: Array<{ content: string; author: string; createdAt: string }> = [];
  if (deal.id) {
    try {
      const tenantId = deal.tenantId ?? getTenantIdSync();
      const live = await fetchPinnedNotesForEntity(tenantId, 'deal', deal.id);
      pinnedFromNotes = live.map((n) => ({
        content: n.content,
        author: n.createdByName,
        createdAt: n.createdAt,
      }));
    } catch (err) {
      console.warn('[deal-workflow] pinned notes read failed, using legacy array only:', err);
    }
  }

  // Dedup on normalised content — a legacy note migrated into the collection
  // would otherwise be pinned onto the job twice.
  const pinnedSeen = new Set<string>();
  const pinnedNotes: Array<{ content: string; author: string; createdAt: string }> = [];
  for (const n of [...pinnedFromNotes, ...pinnedFromLegacy]) {
    const key = (n.content ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    if (!key || pinnedSeen.has(key)) continue;
    pinnedSeen.add(key);
    pinnedNotes.push(n);
  }
  if (pinnedNotes.length > 0) snapshot.pinnedNotes = pinnedNotes;

  // Include install plan so AI briefing knows visit context
  if (deal.installPlan) {
    snapshot.installPlan = {
      visitCount: deal.installPlan.visitCount,
      hoursPerVisit: deal.installPlan.hoursPerVisit,
      customerPreferredDate: deal.installPlan.customerPreferredDate,
      notes: deal.installPlan.notes,
    };
  }
  if (deal.dealOwnerName) snapshot.dealOwnerName = deal.dealOwnerName;
  if (deal.criticalNotes) snapshot.criticalNotes = deal.criticalNotes;
  if (deal.gateCode) snapshot.gateCode = deal.gateCode;
  if (deal.siteAccessInstructions) snapshot.siteAccessInstructions = deal.siteAccessInstructions;

  // Pre-site completion note (synced from pre-site job on completion)
  const dealPresiteNote = (deal as { presiteCompletionNote?: string }).presiteCompletionNote;
  if (dealPresiteNote) {
    snapshot.presiteCompletionNote = dealPresiteNote;
  }

  // Install-tech briefing — offline fallback for the tech modal's
  // FieldBriefingCard (which prefers a live deal read for freshness).
  if (deal.techBriefing) snapshot.techBriefing = deal.techBriefing;

  // Field scope (includes variations for install techs to check on-site).
  // Multi-scope pre-sites populate fieldScopes[]; keep the legacy fieldScope
  // mirror so older snapshot consumers that only read fieldScope still work.
  const snapshotScopes = snapshot as DealSnapshot & {
    fieldScopes?: Deal['fieldScopes'];
    fieldScope?: Deal['fieldScope'];
  };
  if (deal.fieldScopes && deal.fieldScopes.length > 0) {
    snapshotScopes.fieldScopes = deal.fieldScopes;
    snapshotScopes.fieldScope = deal.fieldScopes[0];
  } else if (deal.fieldScope) {
    snapshotScopes.fieldScope = deal.fieldScope;
  }

  return snapshot;
}

/**
 * Create a PRE-SITE job from a deal entering the PRESITE stage.
 * Auto-called when a deal moves to PRESITE. The job lands in the
 * ops/scheduling system as "Unscheduled" with full deal context
 * so the scheduler can assess scope (large vs small pre-site).
 *
 * @returns The newly created job ID
 */
export async function createPresiteJobFromDeal(
  dealId: string,
  userId: string,
  userName: string,
): Promise<string> {
  const dealRef = doc(db, 'deals', dealId);
  const snap = await getDoc(dealRef);
  if (!snap.exists()) throw new Error(`Deal ${dealId} not found`);

  const deal = { id: snap.id, ...snap.data() } as Deal;

  // Mirror the deal's per-service intake into the unified notes collection so
  // it reaches the scheduler side panel + the tech's field view (the office
  // brief only carries the general call note). Idempotent + self-catching;
  // the guard skips the transaction when it's already been seeded.
  if (!deal.intakeNotesSeeded) {
    await seedDealIntakeNotes(dealId, userId, userName);
  }

  // Don't create duplicate jobs
  if (deal.createdJobId) {
    console.warn(`Deal ${dealId} already has a job: ${deal.createdJobId}`);
    return deal.createdJobId;
  }

  // Fetch linked customer for extra context.
  // If the deal references a customer that no longer exists, fail loud:
  // creating a job with a dangling customer FK strips gate codes, access
  // instructions, and contact info from the tech's job — a safety issue
  // when the tech shows up to a site they can't enter. (FIX_PLAN HP1)
  let customerData: Record<string, unknown> = {};
  if (deal.customerId) {
    const custSnap = await getDoc(doc(db, 'customers', deal.customerId));
    if (!custSnap.exists()) {
      throw new Error(
        `Customer ${deal.customerId} referenced by deal ${dealId} not found — fix deal's customer link before creating job.`,
      );
    }
    customerData = custSnap.data();
  }

  const now = new Date().toISOString();
  const serviceLabels = (deal.selectedServices ?? [])
    .map((s: DealCategory) => DEAL_CATEGORY_LABELS[s])
    .join(', ');
  const dealSnapshot = await buildDealSnapshot(deal);

  const tenantId = deal.tenantId ?? getTenantIdSync();
  const presiteCoords = await resolveJobCoords(deal, customerData.address as string);
  const guidance = (deal as DealWithGuidance).schedulingGuidance;

  const newJob: Record<string, unknown> = {
    jobType: 'job',
    client: deal.customerName ?? deal.contactName ?? 'Unknown',
    type: 'Pre-Site',
    status: 'Unscheduled',

    // Tenant
    tenantId,

    // Audit timeline — who made the task
    eventLog: [buildJobEvent('created', 'Pre-Site job created from deal', { actor: userId, actorName: userName })],

    // Location
    address: deal.jobAddress ?? (customerData.address as string) ?? '',
    city: deal.jobCity ?? (customerData.city as string) ?? '',
    state: deal.jobState ?? (customerData.state as string) ?? '',
    postcode: deal.jobZipCode ?? (customerData.postcode as string) ?? (customerData.zipCode as string) ?? '',
    loc: deal.jobCity ?? (customerData.city as string) ?? '',
    lat: presiteCoords.lat,
    lng: presiteCoords.lng,
    ...(presiteCoords.formatted
      ? { geocodedAt: new Date().toISOString(), geocodedAddress: presiteCoords.formatted }
      : {}),

    // Scheduling - Unscheduled (ops will schedule via drag-drop)
    date: '',
    time: '09:00',
    duration: guidance?.estimatedDuration ?? 60,

    // Scheduling guidance from office (for scheduler reference)
    ...(guidance ? {
      schedulingGuidance: {
        preferredTechId: guidance.preferredTechId,
        preferredTechName: guidance.preferredTechName,
        preferredTechIds: guidance.preferredTechIds || [],
        preferredTechNames: guidance.preferredTechNames || [],
        customerPreferredDate: guidance.customerPreferredDate,
        customerPreferredTime: guidance.customerPreferredTime,
        scheduleByDate: guidance.scheduleByDate,
      },
    } : {}),

    // Services - store both enum values and human-readable labels
    services: deal.selectedServices ?? [],
    // Office brief intentionally blank for deal jobs — the type / customer /
    // services live in the modal header, and the real context (intake, call
    // notes, scheduling guidance) now flows as timestamped notes in the
    // tech's Notes timeline rather than a boilerplate blob pinned to the top.
    notes: '',
    // Office scheduling guidance → the timestamped "scheduler" note.
    ...(guidance?.schedulingNotes ? {
      schedulingNotes: guidance.schedulingNotes,
      schedulingNotesAt: now,
      schedulingNotesBy: deal.dealOwnerName || userName,
    } : {}),

    // Contact
    customerId: deal.customerId ?? null,
    contactName: deal.contactName ?? (customerData.name as string) ?? '',
    contact: deal.contactPhone ?? (customerData.phone as string) ?? '',
    contactEmail: deal.contactEmail ?? (customerData.email as string) ?? '',

    // Critical info
    criticalNotes: deal.criticalNotes ?? (customerData.accessInstructions as string) ?? '',
    gateCode: deal.gateCode ?? '',
    siteAccessInstructions: deal.siteAccessInstructions ?? '',

    // Tech assignment - none yet (scheduler assigns)
    techIds: [],
    techId: '',

    // Deal link + category for module loading
    dealId: dealId,
    dealCategory: deal.selectedServices?.[0] ?? '',

    // Full deal context snapshot
    dealSnapshot,

    // Defaults
    checklist: [],
    deleted: false,
    sos: false,
    createdAt: now,
  };

  // Auto-attach checklist template checkpoints (non-blocking)
  const preSiteDealCategory = deal.selectedServices?.[0] ?? '';
  if (preSiteDealCategory) {
    try {
      const templateCheckpoints = await getCheckpointsForNewJob(
        tenantId, preSiteDealCategory, 'Pre-Site'
      );
      if (templateCheckpoints) {
        newJob.checkpoints = templateCheckpoints;
      }
    } catch (checklistErr) {
      console.warn('⚠️ Failed to load checklist template for pre-site job (non-blocking):', checklistErr);
      // Don't block job creation if checklist template lookup fails
    }
  }

  // Undefined values are stripped DEEPLY at the batch write below via
  // stripUndefinedDeep — a top-level key sweep can't reach nested undefineds
  // inside dealSnapshot (installPlan / fieldScopes / dealNotes), plannedMaterials,
  // or checkpoints, which Firestore rejects wholesale ("Unsupported field value:
  // undefined"). Mirrors the fault-side fix in fault.service.ts (PR #1267).

  // Pre-allocate the job's DocumentReference so the deal's createdJobId
  // forward link can be written in the same atomic batch as the job
  // itself. Pre-fix this was addDoc + updateDoc — if the deal write failed,
  // the job existed (with dealId reverse link) but the deal was missing
  // the forward link. Tracked as M2.
  const jobRef = doc(collection(db, 'jobs'));

  const logEntry: Record<string, unknown> = {
    id: crypto.randomUUID(),
    timestamp: now,
    actor: userId,
    actorName: userName,
    action: 'Pre-site job created',
    details: `Unscheduled pre-site job ${jobRef.id} created. Services: ${serviceLabels}`,
    automated: true,
  };

  const batch = writeBatch(db);
  batch.set(jobRef, stripUndefinedDeep(newJob));
  batch.update(dealRef, {
    createdJobId: jobRef.id,
    activityLog: arrayUnion(logEntry),
    updatedAt: Timestamp.now(),
  });
  await batch.commit();

  return jobRef.id;
}

/**
 * Create a job in the `jobs` collection from a deal.
 * Called when a deal is ready for scheduling/installation.
 *
 * This bridges the deal workflow into the tech schedule:
 * - Copies customer, address, services, critical notes from the deal
 * - Embeds a full DealSnapshot so techs have complete context
 * - Links the job back to the deal (bidirectional via dealId / createdJobId)
 * - Logs the conversion in the deal's activity feed
 *
 * @returns The newly created job ID
 */
export async function createJobFromDeal(
  dealId: string,
  userId: string,
  userName: string,
  schedule?: {
    date: string;
    time: string;
    duration?: number;
    techIds?: string[];
    /** Denormalised names for `techIds` — needed when they ride as preferred
     *  techs (see the tech-assignment block below), since the queue card and
     *  job-detail "Scheduling Guidance" both render names, not ids. */
    techNames?: string[];
  },
  /** If true, allows creating an additional job even if installJobId already set */
  allowAdditional = false,
  /** Per-visit metadata from multi-visit builder */
  visitMeta?: {
    visitName?: string;
    visitNumber?: number;
    totalVisits?: number;
    allocatedItemIds?: string[];
    allocatedItemNames?: string[];
  },
): Promise<string> {
  const dealRef = doc(db, 'deals', dealId);
  const snap = await getDoc(dealRef);
  if (!snap.exists()) throw new Error(`Deal ${dealId} not found`);

  const deal = { id: snap.id, ...snap.data() } as Deal;

  // Mirror the deal's per-service intake into the unified notes collection so
  // it reaches the scheduler side panel + the tech's field view (the office
  // brief only carries the general call note). Idempotent + self-catching;
  // the guard skips the transaction when it's already been seeded.
  if (!deal.intakeNotesSeeded) {
    await seedDealIntakeNotes(dealId, userId, userName);
  }

  // Don't create duplicate install jobs (unless explicitly creating an additional visit)
  if (deal.installJobId && !allowAdditional) {
    console.warn(`Deal ${dealId} already has an install job: ${deal.installJobId}`);
    return deal.installJobId;
  }

  // Fetch linked customer for extra context.
  // If the deal references a customer that no longer exists, fail loud:
  // creating a job with a dangling customer FK strips gate codes, access
  // instructions, and contact info from the tech's job — a safety issue
  // when the tech shows up to a site they can't enter. (FIX_PLAN HP1)
  let customerData: Record<string, unknown> = {};
  if (deal.customerId) {
    const custSnap = await getDoc(doc(db, 'customers', deal.customerId));
    if (!custSnap.exists()) {
      throw new Error(
        `Customer ${deal.customerId} referenced by deal ${dealId} not found — fix deal's customer link before creating job.`,
      );
    }
    customerData = custSnap.data();
  }

  const now = new Date().toISOString();
  const serviceLabels = (deal.selectedServices ?? [])
    .map((s: DealCategory) => DEAL_CATEGORY_LABELS[s])
    .join(', ');
  const dealSnapshot = await buildDealSnapshot(deal);

  const tenantId = deal.tenantId ?? getTenantIdSync();
  const installCoords = await resolveJobCoords(deal, customerData.address as string);
  const guidance = (deal as DealWithGuidance).schedulingGuidance;
  // Quoted labour rolled up from the deal's scopes — stamped on the job so
  // schedulers see what was QUOTED (hours + crew) next to the scheduled
  // duration, and used as the last duration fallback for flows that skip
  // the scheduling modal (parallel Schedule & Provision).
  const quotedLabour = summariseScopeLabour(deal.quoteScopes);
  const quotedDurationMins =
    quotedLabour.hours > 0 ? Math.round(quotedLabour.hours * 60) : undefined;

  // Techs picked per-visit (the office's multi-visit builder, or the techs in
  // the PM meeting) are a PREFERENCE for whoever schedules the job — not a
  // crew assignment. Every caller of this function creates the job with an
  // empty date, so it lands in the scheduling queue: hard-assigning `techIds`
  // there makes it read as "already crewed" while nothing is booked, and both
  // surfaces that answer "who should this go to?" (the SchedulerV3 queue card
  // and the job-detail Scheduling Guidance panel) read `schedulingGuidance`,
  // not `techIds`. Only a schedule carrying a real date actually crews a job.
  const isDatedPlacement = !!schedule?.date;
  const visitPreferredTechIds = isDatedPlacement ? [] : (schedule?.techIds ?? []);
  const visitPreferredTechNames = isDatedPlacement ? [] : (schedule?.techNames ?? []);
  // Per-visit picks beat the deal-level guidance list — they're the more
  // specific choice, made per visit rather than once for the whole deal.
  const mergedGuidance =
    guidance || visitPreferredTechIds.length > 0
      ? {
          preferredTechId: guidance?.preferredTechId,
          preferredTechName: guidance?.preferredTechName,
          preferredTechIds:
            visitPreferredTechIds.length > 0
              ? visitPreferredTechIds
              : guidance?.preferredTechIds || [],
          preferredTechNames:
            visitPreferredTechIds.length > 0
              ? visitPreferredTechNames
              : guidance?.preferredTechNames || [],
          customerPreferredDate: guidance?.customerPreferredDate,
          customerPreferredTime: guidance?.customerPreferredTime,
          scheduleByDate: guidance?.scheduleByDate,
        }
      : undefined;

  const newJob: Record<string, unknown> = {
    jobType: 'job',
    client: deal.customerName ?? deal.contactName ?? 'Unknown',
    type: 'Install',
    status: schedule?.date ? 'Scheduled' : 'Unscheduled',

    // Tenant
    tenantId,

    // Audit timeline — who made the task
    eventLog: [buildJobEvent('created', 'Install job created from deal', { actor: userId, actorName: userName })],

    // Location
    address: deal.jobAddress ?? (customerData.address as string) ?? '',
    city: deal.jobCity ?? (customerData.city as string) ?? '',
    state: deal.jobState ?? (customerData.state as string) ?? '',
    postcode: deal.jobZipCode ?? (customerData.postcode as string) ?? (customerData.zipCode as string) ?? '',
    loc: deal.jobCity ?? (customerData.city as string) ?? '',
    lat: installCoords.lat,
    lng: installCoords.lng,
    ...(installCoords.formatted
      ? { geocodedAt: new Date().toISOString(), geocodedAddress: installCoords.formatted }
      : {}),

    // Scheduling - Unscheduled (ops will schedule via calendar)
    date: schedule?.date ?? '',
    time: schedule?.time ?? '09:00',
    duration: schedule?.duration ?? guidance?.estimatedDuration ?? quotedDurationMins ?? 120,

    // Quoted labour (display + scheduler reference) — see summariseScopeLabour
    ...(quotedLabour.hours > 0 ? { quotedLaborHours: quotedLabour.hours } : {}),
    ...(quotedLabour.techCount > 0 ? { quotedTechCount: quotedLabour.techCount } : {}),

    // Scheduling guidance from office (for scheduler reference)
    ...(mergedGuidance ? { schedulingGuidance: mergedGuidance } : {}),

    // Services
    services: deal.selectedServices ?? [],
    // Office brief intentionally blank for deal jobs — the type / customer /
    // services live in the modal header, and the real context (intake, call
    // notes, scheduling guidance) now flows as timestamped notes in the
    // tech's Notes timeline rather than a boilerplate blob pinned to the top.
    notes: '',
    // Office scheduling guidance → the timestamped "scheduler" note.
    ...(guidance?.schedulingNotes ? {
      schedulingNotes: guidance.schedulingNotes,
      schedulingNotesAt: now,
      schedulingNotesBy: deal.dealOwnerName || userName,
    } : {}),

    // Contact
    customerId: deal.customerId ?? null,
    contactName: deal.contactName ?? (customerData.name as string) ?? '',
    contact: deal.contactPhone ?? (customerData.phone as string) ?? '',
    contactEmail: deal.contactEmail ?? (customerData.email as string) ?? '',

    // Critical info
    criticalNotes: deal.criticalNotes ?? (customerData.accessInstructions as string) ?? '',
    gateCode: deal.gateCode ?? '',
    siteAccessInstructions: deal.siteAccessInstructions ?? '',

    // Tech assignment — only a dated placement crews the job; otherwise the
    // picks ride as preferred techs in `schedulingGuidance` above.
    techIds: isDatedPlacement ? (schedule?.techIds ?? []) : [],
    techId: isDatedPlacement ? (schedule?.techIds?.[0] ?? '') : '',

    // Deal link + category for module loading
    dealId: dealId,
    dealCategory: deal.selectedServices?.[0] ?? '',

    // Full deal context snapshot
    dealSnapshot,

    // Defaults
    checklist: [],
    deleted: false,
    sos: false,
    createdAt: now,
  };

  // Apply visit-specific metadata from multi-visit builder
  if (visitMeta) {
    if (visitMeta.visitName) newJob.visitName = visitMeta.visitName;
    if (visitMeta.visitNumber) newJob.visitNumber = visitMeta.visitNumber;
    if (visitMeta.totalVisits) newJob.totalVisits = visitMeta.totalVisits;
    if (visitMeta.allocatedItemIds?.length) newJob.allocatedItemIds = visitMeta.allocatedItemIds;
    if (visitMeta.allocatedItemNames?.length) newJob.allocatedItemNames = visitMeta.allocatedItemNames;
    // Per-visit scope as a short office note (the visit name + customer live
    // on the card header already). Blank when there's no item allocation.
    const itemList = visitMeta.allocatedItemNames?.join(', ') || '';
    newJob.notes = itemList ? `This visit: ${itemList}` : '';
  }

  // Attach provisioning items as plannedMaterials on the job (for tech visibility + AI summary)
  try {
    const { getProvisioningItemsByDeal } = await import('./provisioning.service');
    const provItems = await getProvisioningItemsByDeal(dealId);

    // For multi-visit deals: filter to items allocated to THIS visit. Items
    // without a metadata.quoteItemId (BP-sourced, kit_order, VoIP porting,
    // activation, etc.) can't be allocated to a specific visit by ID, so they
    // pass through on every visit — matches existing behaviour for those item
    // types. Single-visit jobs have visitMeta.allocatedItemIds undefined and
    // see every item, also unchanged.
    const allocated = visitMeta?.allocatedItemIds;
    const visitItems = allocated !== undefined
      ? provItems.filter((item) => {
          const qid = (item.metadata as { quoteItemId?: string } | undefined)?.quoteItemId;
          if (!qid) return true; // unallocatable — every visit gets it
          return allocated.includes(qid);
        })
      : provItems;

    if (visitItems.length > 0) {
      newJob.plannedMaterials = visitItems.map((rawItem, idx: number) => {
        const item = rawItem as typeof rawItem & {
          partId?: string;
          name?: string;
          category?: string;
          sku?: string;
        };
        return {
          id: item.id || item.partId || `prov-${idx}`,
          name: item.name || item.itemName || 'Unknown item',
          quantity: item.quantity ?? 1,
          status: item.status || 'pending',
          category: item.category || '',
          sku: item.sku || '',
          supplier: item.supplier || '',
        };
      });
      newJob.provisioningStatus = 'picked';
    }
  } catch (provErr) {
    console.warn('[Install Job] Failed to load provisioning items (non-blocking):', provErr);
  }

  // Auto-attach checklist template checkpoints (non-blocking)
  const installDealCategory = deal.selectedServices?.[0] ?? '';
  if (installDealCategory) {
    try {
      const templateCheckpoints = await getCheckpointsForNewJob(
        tenantId, installDealCategory, 'Install'
      );
      if (templateCheckpoints) {
        newJob.checkpoints = templateCheckpoints;
      }
    } catch (checklistErr) {
      console.warn('⚠️ Failed to load checklist template for install job (non-blocking):', checklistErr);
    }
  }

  // Undefined values are stripped DEEPLY at the batch write below via
  // stripUndefinedDeep — a top-level key sweep can't reach nested undefineds
  // inside dealSnapshot (installPlan / fieldScopes / dealNotes), plannedMaterials,
  // or checkpoints, which Firestore rejects wholesale ("Unsupported field value:
  // undefined"). Mirrors the fault-side fix in fault.service.ts (PR #1267).

  // Pre-allocate the job ref so the deal's installJobId/installJobIds
  // forward link writes in the same batch as the job itself (M2).
  const jobRef = doc(collection(db, 'jobs'));

  const existingJobIds = deal.installJobIds ?? (deal.installJobId ? [deal.installJobId] : []);
  const visitNumber = existingJobIds.length + 1;

  const logEntry: Record<string, unknown> = {
    id: crypto.randomUUID(),
    timestamp: now,
    actor: userId,
    actorName: userName,
    action: visitNumber > 1 ? `Install visit ${visitNumber} created` : 'Install job created',
    details: `${visitNumber > 1 ? `Visit ${visitNumber} — ` : ''}Unscheduled install job ${jobRef.id} created. Services: ${serviceLabels}`,
    automated: true,
  };

  const batch = writeBatch(db);
  batch.set(jobRef, stripUndefinedDeep(newJob));
  batch.update(dealRef, {
    installJobId: jobRef.id,
    installJobIds: arrayUnion(jobRef.id),
    activityLog: arrayUnion(logEntry),
    updatedAt: Timestamp.now(),
  });
  await batch.commit();

  return jobRef.id;
}

/**
 * Create an additional install visit for a deal that already has install jobs.
 * Used for multi-scope deals that require separate visits (e.g., WiFi one day, Security next).
 */
export async function createAdditionalInstallJob(
  dealId: string,
  userId: string,
  userName: string,
): Promise<string> {
  return createJobFromDeal(dealId, userId, userName, undefined, true);
}

// ============================================================================
// PARALLEL SCHEDULE & PROVISION
// ============================================================================

/**
 * Schedule & Provision a deal in parallel.
 *
 * Used for quick-to-provision deals (e.g. Starlink kits) where you want to
 * get the job on the schedule immediately while provisioning happens in the
 * background. The deal jumps from WON → SCHEDULING, skipping the
 * PROVISIONING stage as a blocker, but provisioning items are still
 * generated and tracked.
 *
 * Flow:
 *   1. Generate provisioning items (same as PROVISIONING stage entry)
 *   2. Move deal to SCHEDULING (which auto-creates the install job)
 *   3. Mark deal.parallelProvisioning = true so both systems show it
 *
 * The install job appears in the scheduler with a "not provisioned" indicator.
 * The deal appears in /provisioning with the scheduled date visible.
 */
export async function scheduleAndProvisionDeal(
  dealId: string,
  userId: string,
  userName: string,
): Promise<string> {
  const dealRef = doc(db, 'deals', dealId);
  const snap = await getDoc(dealRef);
  if (!snap.exists()) throw new Error(`Deal ${dealId} not found`);

  const deal = { id: snap.id, ...snap.data() } as Deal;

  // Guard: only allowed from WON stage
  if (deal.stage !== DealStage.WON) {
    throw new Error(`Cannot schedule & provision: deal must be at WON stage, currently at ${deal.stage}`);
  }

  const now = new Date().toISOString();

  // ── Step 1: Generate provisioning items (mirrors PROVISIONING stage entry) ──
  try {
    const existingItems = await getProvisioningItemsByDeal(dealId);
    if (existingItems.length === 0) {
      let quoteItems: PlaygroundQuoteItem[] = [];

      // Priority 1: quoteReviewedItems (approved + visible only)
      // Items flagged hideFromProvisioning stay in deal value + billing but are
      // suppressed from provisioning_items (and therefore from the tech job).
      if (deal.quoteReviewedItems && deal.quoteReviewedItems.length > 0) {
        const approved = deal.quoteReviewedItems.filter(
          (ri: ReviewedQuoteItem) => ri.approved && !ri.hideFromProvisioning,
        );
        quoteItems = approved.map((ri: ReviewedQuoteItem) => {
          const unitPrice = ri.revisedUnitPrice || ri.originalUnitPrice;
          return {
            id: ri.originalItemId,
            name: ri.name,
            category: ri.category,
            quantity: ri.revisedQuantity || ri.originalQuantity,
            unitCost: unitPrice,
            unitPrice,
            totalPrice: ri.revisedTotalPrice || ri.originalTotalPrice,
            billingType: ri.billingType,
            pipelineStatus: 'quoted' as const,
            sourceType: 'manual' as const,
            addedAt: ri.reviewedAt,
            updatedAt: ri.reviewedAt,
          };
        });
      }
      // Priority 2: quoteScopes
      else if (deal.quoteScopes && deal.quoteScopes.length > 0) {
        quoteItems = deal.quoteScopes.flatMap((scope) => scope.items || []);
      }
      // Priority 3: latest quoteVersion items
      else if (deal.quoteVersions && deal.quoteVersions.length > 0) {
        const latestVersion = deal.quoteVersions[deal.quoteVersions.length - 1];
        if (latestVersion.scopes && latestVersion.scopes.length > 0) {
          quoteItems = latestVersion.scopes.flatMap((scope) => scope.items || []);
        } else if (latestVersion.items && latestVersion.items.length > 0) {
          quoteItems = latestVersion.items;
        }
      }
      // Priority 4: proposalAcceptedItems from Better Proposals webhook
      else if (deal.proposalAcceptedItems && deal.proposalAcceptedItems.length > 0) {
        const now = new Date().toISOString();
        quoteItems = deal.proposalAcceptedItems
          .filter((ai) => ai.isSelected !== false)
          .map((ai) => ({
            id: `bp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: ai.label,
            category: ai.tableTitle || undefined,
            quantity: ai.quantity || 1,
            unitCost: ai.unitCost || 0,
            unitPrice: ai.unitCost || 0,
            totalPrice: ai.cost || 0,
            billingType: (ai.recurringType && ai.recurringType !== 'once' ? 'recurring' : 'one-time') as 'one-time' | 'recurring',
            pipelineStatus: 'approved' as const,
            sourceType: 'manual' as const,
            addedAt: now,
            updatedAt: now,
          }));
      }

      if (quoteItems.length > 0) {
        await generateProvisioningFromQuote(dealId, quoteItems, deal);
        console.log(`[Schedule & Provision] Generated provisioning items for deal ${dealId}`);
      }
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[Schedule & Provision] Failed to generate provisioning items:', err);
    // Don't block — provisioning items can be added manually. But surface
    // so ops sees this deal landed in SCHEDULING + parallel-provisioning
    // mode without items, instead of relying on /provisioning observation
    // (M3).
    await recordStageTransitionError(
      dealRef,
      DealStage.SCHEDULING,
      'parallel-provisioning-generation',
      message,
    );
  }

  // ── Step 2: Mark deal as parallel provisioning + move to SCHEDULING ──
  // Set parallelProvisioning before moveDealToStage so the flag is visible
  // when createJobFromDeal reads the deal document.
  await updateDoc(dealRef, {
    parallelProvisioning: true,
    parallelProvisioningAt: now,
  });

  // Move to SCHEDULING (auto-creates install job via createJobFromDeal).
  // If the stage move fails, unwind the parallelProvisioning flag so the
  // /provisioning page doesn't treat the deal as parallel when no install
  // job actually exists. (FIX_PLAN HP4)
  try {
    await moveDealToStage(dealId, DealStage.SCHEDULING, userId, userName, 'Schedule & Provision — provisioning in parallel');
  } catch (err) {
    console.error('[Schedule & Provision] Failed to move to SCHEDULING — unwinding parallelProvisioning flag:', err);
    try {
      await updateDoc(dealRef, {
        parallelProvisioning: deleteField(),
        parallelProvisioningAt: deleteField(),
      });
    } catch (unwindErr) {
      console.error('[Schedule & Provision] Failed to unwind parallelProvisioning flag:', unwindErr);
    }
    throw err; // Re-throw — caller needs to know the stage move failed
  }

  // Log the parallel provisioning action (non-blocking)
  try {
    await addActivityLogEntry(
      dealId,
      userId,
      userName,
      'Schedule & Provision triggered',
      'Job scheduled while provisioning runs in parallel. Track gear readiness in /provisioning.',
      false,
    );
  } catch (err) {
    console.error('[Schedule & Provision] Failed to log activity:', err);
    // Don't re-throw — activity log is secondary to deal state
  }

  return dealId;
}

// ============================================================================
// QUICK RAISE JOB
// ============================================================================

/**
 * Quick-raise a job directly from a deal — regardless of deal stage.
 *
 * Used for ad-hoc revisits, follow-ups, or urgent callouts where you don't
 * want to create a new deal. The job links back to the deal and carries
 * full customer/address/service context. Lands as Unscheduled.
 *
 * Does NOT advance or change the deal stage — purely creates a linked job.
 */
export async function quickRaiseJobFromDeal(
  dealId: string,
  userId: string,
  userName: string,
  details: {
    jobType: 'Install' | 'Fault' | 'Pre-Site' | 'Task';
    reason: string;
    estimatedDuration: number;
    preferredTechIds?: string[];
    preferredTechNames?: string[];
  },
): Promise<string> {
  const dealRef = doc(db, 'deals', dealId);
  const snap = await getDoc(dealRef);
  if (!snap.exists()) throw new Error(`Deal ${dealId} not found`);

  const deal = { id: snap.id, ...snap.data() } as Deal;

  // Mirror the deal's per-service intake into the unified notes collection so
  // it reaches the scheduler side panel + the tech's field view (the office
  // brief only carries the general call note). Idempotent + self-catching;
  // the guard skips the transaction when it's already been seeded.
  if (!deal.intakeNotesSeeded) {
    await seedDealIntakeNotes(dealId, userId, userName);
  }

  // Fetch linked customer for extra context.
  // If the deal references a customer that no longer exists, fail loud:
  // creating a job with a dangling customer FK strips gate codes, access
  // instructions, and contact info from the tech's job — a safety issue
  // when the tech shows up to a site they can't enter. (FIX_PLAN HP1)
  let customerData: Record<string, unknown> = {};
  if (deal.customerId) {
    const custSnap = await getDoc(doc(db, 'customers', deal.customerId));
    if (!custSnap.exists()) {
      throw new Error(
        `Customer ${deal.customerId} referenced by deal ${dealId} not found — fix deal's customer link before creating job.`,
      );
    }
    customerData = custSnap.data();
  }

  const now = new Date().toISOString();
  const tenantId = deal.tenantId ?? getTenantIdSync();
  const dealSnapshot = await buildDealSnapshot(deal);

  const quickRaiseCoords = await resolveJobCoords(deal, customerData.address as string);

  const newJob: Record<string, unknown> = {
    jobType: 'job',
    client: deal.customerName ?? deal.contactName ?? 'Unknown',
    type: details.jobType,
    status: 'Unscheduled',
    tenantId,

    // Audit timeline — who made the task
    eventLog: [buildJobEvent('created', `${details.jobType} job quick-raised from deal: ${details.reason}`, { actor: userId, actorName: userName })],

    // Location
    address: deal.jobAddress ?? (customerData.address as string) ?? '',
    city: deal.jobCity ?? (customerData.city as string) ?? '',
    state: deal.jobState ?? (customerData.state as string) ?? '',
    postcode: deal.jobZipCode ?? (customerData.postcode as string) ?? (customerData.zipCode as string) ?? '',
    loc: deal.jobCity ?? (customerData.city as string) ?? '',
    lat: quickRaiseCoords.lat,
    lng: quickRaiseCoords.lng,
    ...(quickRaiseCoords.formatted
      ? { geocodedAt: new Date().toISOString(), geocodedAddress: quickRaiseCoords.formatted }
      : {}),

    // Scheduling — unscheduled, ops will drag onto calendar
    date: '',
    time: '09:00',
    duration: details.estimatedDuration,

    // Services
    services: deal.selectedServices ?? [],
    // The raise reason IS the office brief for this job; deal intake / call
    // notes already flow as their own timestamped notes via the deal link.
    notes: details.reason,

    // Contact
    customerId: deal.customerId ?? null,
    contactName: deal.contactName ?? (customerData.name as string) ?? '',
    contact: deal.contactPhone ?? (customerData.phone as string) ?? '',
    contactEmail: deal.contactEmail ?? (customerData.email as string) ?? '',

    // Critical info
    criticalNotes: deal.criticalNotes ?? (customerData.accessInstructions as string) ?? '',
    gateCode: deal.gateCode ?? '',
    siteAccessInstructions: deal.siteAccessInstructions ?? '',

    // Tech preference (not assigned — scheduler places them)
    techIds: [],
    techId: '',
    ...(details.preferredTechIds?.length ? {
      schedulingGuidance: {
        preferredTechIds: details.preferredTechIds,
        preferredTechNames: details.preferredTechNames || [],
      },
    } : {}),

    // Deal link
    dealId,
    dealCategory: deal.selectedServices?.[0] ?? '',
    dealSnapshot,

    // Metadata
    quickRaised: true,
    quickRaisedReason: details.reason,
    quickRaisedAt: now,
    quickRaisedBy: userId,
    quickRaisedByName: userName,

    // Defaults
    checklist: [],
    deleted: false,
    sos: false,
    createdAt: now,
  };

  // Attach provisioning items as plannedMaterials for Install jobs (non-blocking)
  if (details.jobType === 'Install') {
    try {
      const provItems = await getProvisioningItemsByDeal(dealId);
      if (provItems.length > 0) {
        newJob.plannedMaterials = provItems.map((rawItem, idx: number) => {
          const item = rawItem as typeof rawItem & {
            partId?: string;
            name?: string;
            category?: string;
            sku?: string;
          };
          return {
            id: item.id || item.partId || `prov-${idx}`,
            name: item.name || item.itemName || 'Unknown item',
            quantity: item.quantity ?? 1,
            status: item.status || 'pending',
            category: item.category || '',
            sku: item.sku || '',
            supplier: item.supplier || '',
          };
        });
        newJob.provisioningStatus = 'picked';
      }
    } catch (provErr) {
      console.warn('[Quick-Raise] Failed to load provisioning items (non-blocking):', provErr);
    }
  }

  // Auto-attach checklist template (non-blocking)
  const category = deal.selectedServices?.[0] ?? '';
  if (category) {
    try {
      const checkpoints = await getCheckpointsForNewJob(tenantId, category, details.jobType);
      if (checkpoints) newJob.checkpoints = checkpoints;
    } catch {
      // Non-blocking — proceed without template
    }
  }

  // Undefined values are stripped DEEPLY at the batch write below via
  // stripUndefinedDeep — a top-level key sweep can't reach nested undefineds
  // inside dealSnapshot (installPlan / fieldScopes / dealNotes), plannedMaterials,
  // or checkpoints, which Firestore rejects wholesale ("Unsupported field value:
  // undefined"). Mirrors the fault-side fix in fault.service.ts (PR #1267).

  // Pre-allocate the job ref so the deal's quickRaisedJobIds forward link
  // writes in the same batch as the job itself (M2).
  const jobRef = doc(collection(db, 'jobs'));

  const logEntry: Record<string, unknown> = {
    id: crypto.randomUUID(),
    timestamp: now,
    actor: userId,
    actorName: userName,
    action: `Quick-raised ${details.jobType} job`,
    details: `${details.reason}`,
    automated: false,
  };

  const batch = writeBatch(db);
  batch.set(jobRef, stripUndefinedDeep(newJob));
  batch.update(dealRef, {
    quickRaisedJobIds: arrayUnion(jobRef.id),
    activityLog: arrayUnion(logEntry),
    updatedAt: Timestamp.now(),
  });
  await batch.commit();

  return jobRef.id;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get the default next action text for a stage
 */
function getNextActionForStage(stage: DealStage): string {
  switch (stage) {
    case DealStage.INTAKE:
      return 'Select services and fill intake details';
    case DealStage.PRESITE:
      return 'Schedule and complete pre-site visit';
    case DealStage.QUOTING:
      return 'Build and review quote';
    case DealStage.PROPOSAL_SENT:
      return 'Awaiting customer response on proposal';
    case DealStage.WON:
      return 'Process provisioning items';
    case DealStage.PROVISIONING:
      return 'Order gear and track delivery';
    case DealStage.SCHEDULING:
      return 'Schedule installation with customer';
    case DealStage.REVIEW:
      return 'Review install report — approve for billing or send back to scheduling';
    case DealStage.BILLING:
      return 'Review services, generate invoice, and complete';
    case DealStage.COMPLETED:
      return 'Deal completed';
    case DealStage.LOST:
      return 'Deal lost';
    default:
      return '';
  }
}
